import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { usePlan } from '../../services/PlanContext';
import { getItem } from '../../utils/localStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  "Comment créer une enquête ?",
  "Où voir mes résultats ?",
  "Comment passer à l'action ?",
];

const ChatbotWidget = () => {
  // Vague 4 (4.9) — le widget s'affichait à TOUS les plans alors que l'API du
  // chatbot est réservée à l'offre supérieure : un client Basic ouvrait
  // l'assistant, posait sa question et recevait systématiquement « une erreur
  // est survenue ». Mieux vaut ne pas promettre la fonctionnalité.
  const { hasFeature, loading: planLoading } = usePlan();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: 'user', content: text.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const companyId = getItem('companyId');
      const res = await axios.post(
        `${BACKEND_URL}/api/v2/chat`,
        {
          message: text.trim(),
          companyId,
          conversationHistory: newMessages.slice(-10),
        },
        { timeout: 60000 }
      );

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: res.data.response },
      ]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Désolé, une erreur est survenue. Réessayez dans un instant." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (planLoading || !hasFeature('ai_advanced')) return null;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 btn btn-circle btn-primary shadow-lg transition"
        title="Assistant IA"
      >
        {isOpen ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-20 right-6 z-50 w-96 bg-white rounded-lg shadow-2xl border border-bsrate overflow-hidden flex flex-col" style={{ maxHeight: '520px' }}>
          {/* Header */}
          <div className="bg-bsrate text-white p-4 shrink-0">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Assistant IA BackStageRate
            </h3>
            <p className="text-xs text-indigo-200 mt-0.5">Votre guide pour naviguer dans BackStageRate</p>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: '200px', maxHeight: '340px' }}>
            {messages.length === 0 && !loading && (
              <div className="text-center py-4">
                <p className="text-sm text-slate-500 mb-3">Comment puis-je vous aider ?</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTIONS.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(suggestion)}
                      className="text-xs px-3 py-1.5 rounded-full border border-bsrate text-bsrate hover:bg-bsrate hover:text-white transition"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                    msg.role === 'user'
                      ? 'bg-bsrate text-white rounded-br-sm'
                      : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 px-3 py-2 rounded-lg rounded-bl-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-bsrate" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t flex gap-2 shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez votre question..."
              className="input input-sm input-bordered flex-1"
              disabled={loading}
            />
            <button
              type="submit"
              className="btn btn-sm btn-circle btn-primary"
              disabled={!input.trim() || loading}
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
